import { useCallback, useEffect, useRef } from "react";
import { getUserPaymentHistory, notifyTravelAllowanceChanged } from "@shared/api";
import { BASE_URL } from "../../api";
import {
    checkPackagePurchaseStatus,
    PENDING_PURCHASE_STORAGE_KEY,
} from "../../utils/packagePurchase";
import { trackYandexGoal, YANDEX_METRIKA_GOALS } from "../../utils/yandexMetrika";

const SUCCESSFUL_PAYMENT_STATUSES = new Set([
    "PAID",
    "CONFIRMED",
    "CAPTURED",
    "SUCCEEDED",
    "SUCCESS",
    "COMPLETED",
]);
const PURCHASE_POLLING_WINDOW_MS = 2 * 60 * 1000;
const SERVER_CLOCK_TOLERANCE_MS = 5 * 60 * 1000;
const PURCHASE_POLL_INTERVAL_MS = 10_000;

const clearPendingPurchase = () => {
    try {
        window.localStorage.removeItem(PENDING_PURCHASE_STORAGE_KEY);
    } catch {
        // Недоступный localStorage не должен ломать приложение.
    }
};

const readPendingPurchase = () => {
    try {
        const raw = window.localStorage.getItem(PENDING_PURCHASE_STORAGE_KEY);
        if (!raw) return null;

        const pending = JSON.parse(raw);
        const startedAtMs = Date.parse(pending?.startedAt);
        if (!pending?.productCode || !Number.isFinite(startedAtMs)) {
            clearPendingPurchase();
            return null;
        }
        return { ...pending, startedAtMs };
    } catch {
        clearPendingPurchase();
        return null;
    }
};

const isConfirmedPurchase = (payment, pending) => {
    if (String(payment?.operation_type || "").toLowerCase() !== "payment") return false;
    if (String(payment?.product_code || "") !== pending.productCode) return false;

    const statuses = [payment?.status, payment?.order_status]
        .map((value) => String(value || "").toUpperCase());
    if (!statuses.some((status) => SUCCESSFUL_PAYMENT_STATUSES.has(status))) return false;

    const confirmedAtMs = Date.parse(payment?.paid_at || payment?.updated_at || payment?.created_at || "");
    return Number.isFinite(confirmedAtMs)
        && confirmedAtMs >= pending.startedAtMs - SERVER_CLOCK_TOLERANCE_MS;
};

const hasSuccessfulStatus = (payload) => (
    [payload?.payment_status, payload?.order_status]
        .map((value) => String(value || "").toUpperCase())
        .some((status) => SUCCESSFUL_PAYMENT_STATUSES.has(status))
);

export default function PurchaseCompletionTracker() {
    const requestInProgressRef = useRef(false);

    const checkPurchase = useCallback(async () => {
        if (requestInProgressRef.current) return;
        if (document.visibilityState === "hidden") return;

        const pending = readPendingPurchase();
        if (!pending) return;

        if (Date.now() - pending.startedAtMs > PURCHASE_POLLING_WINDOW_MS) {
            clearPendingPurchase();
            return;
        }

        const token = window.localStorage.getItem("accessToken");
        if (!token) return;

        requestInProgressRef.current = true;
        try {
            const statusPayload = await checkPackagePurchaseStatus({
                orderId: pending.orderId,
                token,
            });
            if (hasSuccessfulStatus(statusPayload)) {
                clearPendingPurchase();
                notifyTravelAllowanceChanged();
                trackYandexGoal(YANDEX_METRIKA_GOALS.purchaseComplete);
                return;
            }

            const history = await getUserPaymentHistory({
                baseUrl: BASE_URL,
                token,
                page: 1,
                pageSize: 100,
            });
            const confirmedPayment = history.items.find((payment) => isConfirmedPurchase(payment, pending));
            if (!confirmedPayment) return;

            // Аналитика может быть заблокирована расширением или антивирусом.
            // Подтверждённый платёж всё равно должен завершить фоновый опрос.
            clearPendingPurchase();
            notifyTravelAllowanceChanged();
            trackYandexGoal(YANDEX_METRIKA_GOALS.purchaseComplete);
        } catch {
            // Статус может обновиться позже: следующая проверка повторит запрос.
        } finally {
            requestInProgressRef.current = false;
        }
    }, []);

    useEffect(() => {
        const pending = readPendingPurchase();
        if (!pending) return undefined;

        const pollingTimeLeft = Math.max(
            0,
            pending.startedAtMs + PURCHASE_POLLING_WINDOW_MS - Date.now()
        );
        if (pollingTimeLeft === 0) {
            clearPendingPurchase();
            return undefined;
        }

        checkPurchase();

        const intervalId = window.setInterval(checkPurchase, PURCHASE_POLL_INTERVAL_MS);
        const stopPollingId = window.setTimeout(() => {
            window.clearInterval(intervalId);
            // Не переносим незавершённый опрос на карточки и следующие сеансы.
            clearPendingPurchase();
        }, pollingTimeLeft);

        return () => {
            window.clearInterval(intervalId);
            window.clearTimeout(stopPollingId);
        };
    }, [checkPurchase]);

    return null;
}
